import "./dataset-style.scss";

import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CRow,
} from "@coreui/react";
import { Link, useParams, useLocation } from "react-router-dom";
import { useGetDataset, useGetDatasetType } from "./services/DatasetServices";

import { ACCEPTED_FILE_TYPES } from "src/constants";
import { Can } from "src/helpers/Auth";
import DatasetInfo from "./DatasetInfo";
import MultiFileUploader from "./uploaders/DocumentUpload";
import { DocumentUploadWithCloud } from "./uploaders/DocumentUploadWithCloud";
import { PropTypes } from "prop-types";
import TabularFileUploader from "./uploaders/FileUpload";
import { useDropzone } from "react-dropzone";
import { useMemo } from "react";

const baseStyle = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "20px",
  borderWidth: 2,
  borderRadius: 2,
  borderColor: "#e6e6e6",
  borderStyle: "dashed",
  backgroundColor: "#fafafa",
  color: "#b0b0b0",
  outline: "none",
  transition: "border .24s ease-in-out",
};

const focusedStyle = {
  borderColor: "#2196f3",
};

const acceptStyle = {
  borderColor: "#00e676",
};

const rejectStyle = {
  borderColor: "#ff1744",
};

async function isUTF8(file) {
  const MAX_FILE_SIZE = 5120;

  const fileSizeKiloBytes = file.size / 1024;
  // import languageEncoding using modules not require
  const languageEncoding = await import("detect-file-encoding-and-language");

  if (fileSizeKiloBytes > MAX_FILE_SIZE) {
    return null;
  } else {
    // Custom validator to check that the file is UTF-8 encoded
    try {
      const fileinfo = await languageEncoding(file);
      if (fileinfo.encoding === "UTF-8") {
        return null;
      } else {
        return {
          code: "file-not-UTF-encoding",
          message: "File must be CSV with UTF-8 Encoding.",
        };
      }
    } catch (error) {
      console.error("Error detecting file encoding:", error);
      return {
        code: "file-encoding-error",
        message: "Error detecting file encoding.",
      };
    }
  }
}

function StyledDropzone(props) {
  const {
    acceptedFiles,
    fileRejections,
    getRootProps,
    getInputProps,
    isFocused,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    // stupid windows only puts out an excel type, even for .csvs and .csv or mimetype does not work
    multiple: false,
    validator: (file) => {
      props.onBigFile(file);
      return isUTF8(file);
    },
    onDropAccepted: (files) => {
      files.forEach((file) => {
        props.onDragSuccess(file);
      });
    },
    onDropRejected: (files) => {
      files.forEach((file) => {
        props.onDragFailed(file);
      });
    },
    accept: ACCEPTED_FILE_TYPES.EXTENSIONS.CSV,
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isFocused, isDragAccept, isDragReject],
  );

  const files = acceptedFiles.map((file) => (
    <span key={file.path}>{file.name}</span>
  ));

  return (
    <div className="container">
      <span>{isDragReject}</span>
      <div {...getRootProps({ style })} data-attr="datasets:form:csv-dropzone">
        <input {...getInputProps()} />
        <h4>Drag and drop a .CSV data file for upload.</h4>
        <p></p>
        <p>
          The first row should contain the column headers, and subsequent rows
          should contain data.
        </p>
        <p>Only .csv files will be accepted.</p>
      </div>
      <div style={{ marginTop: "1em" }}>
        {/* Success */}
        {files.length > 0 && <CAlert color="success">{files} accepted.</CAlert>}
        {/* Reject */}
        {fileRejections.length > 0 && (
          <div>
            <CAlert color="danger">
              {fileRejections[0].file.path} has failed validation.
              <p></p>
              {fileRejections[0].errors.map((error) => {
                return (
                  <div key={error.code}>
                    <b>Error:</b> {error.code}
                    <br></br>
                    <b>Error Message: </b> {error.message}
                  </div>
                );
              })}
            </CAlert>
          </div>
        )}
        {props.isBigFile && (
          <div>
            <CAlert color="warning">
              <div>
                <b>Warning:</b> File size is too large to confirm that it is
                UTF-8 encoded. Please ensure the file is UTF-8 encoded before
                continuing.
                <br></br>
              </div>
            </CAlert>
          </div>
        )}
      </div>
    </div>
  );
}

StyledDropzone.propTypes = {
  onDragSuccess: PropTypes.func,
  onDragFailed: PropTypes.func,
  isBigFile: PropTypes.bool,
  onBigFile: PropTypes.func,
};

StyledDropzone.default = {
  isBigFile: false,
};

const UploadToDatasetHeader = () => {
  const { dataset_id } = useParams();
  return (
    <CCardHeader data-testid="ds-upload-heading">
      <CRow className="align-items-center">
        <CCol md="10">
          <DatasetInfo
            dataset_id={dataset_id}
            just_name_and_description={true}
          />
        </CCol>
        <CCol md="2" className="text-right">
          <Link to={`/datasets/${dataset_id}/viewer`}>
            <CButton
              color="primary"
              size="sm"
              data-testid="ds-upload-btn-view-dataset"
              data-attr="datasets:btn:view-dataset-from-upload"
            >
              View Dataset
            </CButton>
          </Link>
        </CCol>
      </CRow>
    </CCardHeader>
  );
};

UploadToDatasetHeader.propTypes = {};

const UploadToDataset = (props) => {
  const { dataset_id } = useParams();
  const location = useLocation();
  // Embedded usage (e.g. dataset scoring) passes the id via props instead of
  // the route param.
  const effectiveDatasetId = props.datasetId ?? dataset_id;
  const { data: datasetTypeInfo, isLoading: isLoadingType } =
    useGetDatasetType(effectiveDatasetId);
  const { data: dataset, isLoading: isLoadingDataset } =
    useGetDataset(effectiveDatasetId);
  // Per-dataset ACL: the role check below (`Can`) only proves the user may
  // write datasets in general. Whether they may write THIS dataset (owner /
  // write grant on a private one) is decided server-side and rides on the
  // dataset payload as my_access — fail closed until it arrives, so a
  // view-only grantee never sees the upload form.
  const canWriteDataset = dataset?.my_access?.can_write === true;

  // Parse ?tab=cloud from URL
  const searchParams = new URLSearchParams(location.search);
  const tabParam = searchParams.get("tab");
  const initialTab = tabParam === "cloud" ? 1 : 0;

  const getUploadComponent = (datasetTypeInfo) => {
    // Show loading state while determining dataset type
    if (isLoadingType) {
      return (
        <CCardBody>
          <div style={{ textAlign: "center", padding: "2rem" }}>Loading...</div>
        </CCardBody>
      );
    }

    switch (datasetTypeInfo?.name) {
      case "tabular":
        // tabular is our legacy file uploader wizard for tabular data, this will have the UI with csv upload and then the detail screen with header and unique column id selection
        return <TabularFileUploader {...props} dataset={dataset} />;
      case "Activity":
        return <TabularFileUploader {...props} dataset={dataset} />;
      case "ProjectAI":
        // Project file types
        return (
          <MultiFileUploader
            {...props}
            fileType={ACCEPTED_FILE_TYPES.EXTENSIONS.PROJECT_XER}
            dataset={dataset}
          />
        );
      case "document":
        return (
          <DocumentUploadWithCloud
            {...props}
            fileType={ACCEPTED_FILE_TYPES.EXTENSIONS.DATASET}
            dataset={dataset}
            initialTab={initialTab}
            showCloudTab={true}
          />
        );
      default:
        // This is the new multi file uploader for all other dataset types which has the upload progress bar
        return <MultiFileUploader {...props} dataset={dataset} />;
    }
  };

  const accessDeniedBody = (
    <CCardBody data-testid="ds-upload-denied">
      You do not have permission to upload data to this dataset.
    </CCardBody>
  );

  return (
    <CCard data-testid="ds-upload-container">
      {/* dataset_data:write — uploading is data-plane ingest, matching the
          upload endpoint and the viewer's Upload button. dataset:write is
          the control-plane capability (creating datasets) and wrongly
          denied ingest-only roles here. */}
      <Can
        perform="dataset_data:write"
        yes={() => {
          if (isLoadingDataset) {
            return (
              <CCardBody>
                <div style={{ textAlign: "center", padding: "2rem" }}>
                  Loading...
                </div>
              </CCardBody>
            );
          }
          if (!canWriteDataset) {
            return accessDeniedBody;
          }
          return getUploadComponent(datasetTypeInfo);
        }}
        no={() => accessDeniedBody}
      />
    </CCard>
  );
};

UploadToDataset.propTypes = {
  titleHide: PropTypes.bool,
  datasetId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onDoneClick: PropTypes.func,
  doneCallback: PropTypes.func,
};

export default UploadToDataset;
